import React from 'react';
import { ActionIcon, Group, Text } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { useNavigate, useSearchParams } from 'react-router';
import { useCurrentClientIfAvailable } from 'src/clients/context/CurrentClientProvider';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { IconButton } from 'src/design-system/IconButton';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import { titleFor } from 'src/financial/financial-data/financial-item/rework/financialItemRework';
import {
  financialsReturnPath,
  RETURN_TO_PARAM,
} from 'src/financial/financial-data/financial-item/financialItemPaths';

type Props = {
  title: string;
  /** Entity type. The reworked title names the product alone, the path above carrying the rest. */
  product?: string;
};

export function FormTitle({ title, product }: Props) {
  const navigate = useNavigate();
  const { client } = useCurrentClientIfAvailable();
  const { isCreateMode } = useDynamicUI();
  const { flagEnabledFinancialRework } = useFlags();
  const [searchParams] = useSearchParams();

  // Back leaves an item the same way saving it does: to the Financials it was opened from,
  // which is the family's when that is where the adviser came from.
  const financialsPath = flagEnabledFinancialRework
    ? financialsReturnPath(client?.id ?? '', searchParams.get(RETURN_TO_PARAM))
    : `/client/${client?.id}/financial-data`;

  // The old layout chose a product on a page of its own, so backing out of creating there lands
  // on the picker again. The reworked Financials adds an item from the table it will appear in
  // and never visits that page, so going back to it would hand the adviser the old screen.
  const backPath =
    isCreateMode && !flagEnabledFinancialRework
      ? `/client/${client?.id}/financial-data/create`
      : financialsPath;

  const goBack = () => navigate(backPath);

  return (
    <Group>
      {flagEnabledFinancialRework ? (
        <IconButton
          variant="secondary"
          size={48}
          onClick={goBack}
          aria-label="Back"
          data-testid="back-button"
        >
          <IconArrowLeft size={20} />
        </IconButton>
      ) : (
        <ActionIcon variant="subtle" onClick={goBack} data-testid="back-button">
          <IconArrowLeft color="black" />
        </ActionIcon>
      )}
      <Text size={'displayMd'} fw={600}>
        {flagEnabledFinancialRework && product ? titleFor(product, title) : title}
      </Text>
    </Group>
  );
}
